<template>
  <nav class="navbar" :class="{ hidden: !isNavbarVisible }">
    <div class="nav-container">
      <RouterLink to="/Overvaagning" class="logo-link">
        <img class="logo" src="/src/components/icons/ECG_Logo.png" alt="ECG Logo" />
      </RouterLink>

      <SectionSwitcher ref="switcherRef" v-model:currentSection="currentSection" v-model:switcherOpen="switcherOpen" />

      <div class="section-nav">
        <div class="nav-links" v-if="navConfig[currentSection]?.length">
          <RouterLink v-for="link in navConfig[currentSection]" :key="link.to" :to="link.to" class="nav-link"
            :class="{ active: link.match($route.path) }">
            {{ link.label }}
          </RouterLink>
        </div>
      </div>

      <div class="user-section">
        <BrugerIcon />
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { navConfig } from '@/config/navConfig';
import BrugerIcon from '@/components/BrugerIcon.vue';
import SectionSwitcher from './SectionSwitcher.vue';

const route = useRoute();
const switcherOpen = ref(false);
const switcherRef = ref(null);
const currentSection = ref('orchestrator');

const isNavbarVisible = ref(true);
let lastScrollY = window.scrollY;

const detectCurrentSection = () => {
  const path = route.path;
  if (path.includes('/Analytics')) {
    currentSection.value = 'analytics';
  } else if (path.includes('/ETL')) {
    currentSection.value = 'etl';
  } else {
    currentSection.value = 'orchestrator';
  }
};

const handleClickOutside = (event) => {
  const el = switcherRef.value?.$el;
  if (switcherOpen.value && el && !el.contains(event.target)) {
    switcherOpen.value = false;
  }
};

const handleScroll = () => {
  const currentScrollY = window.scrollY;
  if (currentScrollY > lastScrollY && currentScrollY > 100) {
    isNavbarVisible.value = false;
  } else {
    isNavbarVisible.value = true;
  }
  lastScrollY = currentScrollY;
};

watch(() => route.path, detectCurrentSection, { immediate: true });

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 1000;
}

.nav-container {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 2rem;
  height: 78px;
  margin: 0 auto;
  gap: 2rem;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.logo-link:hover {
  opacity: 0.8;
}

.logo {
  height: 60px;
  width: auto;
}

.section-nav {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  pointer-events: none;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: auto;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  text-decoration: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  position: relative;
}

.nav-link:hover {
  color: #334155;
  background: rgba(0, 0, 0, 0.04);
}

.nav-link.active {
  color: var(--highlights);
  background: var(--highlights-background);
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: var(--highlights);
  border-radius: 50%;
}

.user-section {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.navbar {
  transition: transform 0.3s ease;
}

.navbar.hidden {
  transform: translateY(-100%);
}
</style>